import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuestionsForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/questions/${token}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || "This link isn't valid");
        return d;
      })
      .then((d) => { setForm(d); setAnswers((d.questions || []).map(() => "")); })
      .catch((e) => setErr(e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await fetch(`${API}/api/public/questions/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Couldn't send — please try again");
      setDone(true);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSending(false);
    }
  };

  const shell = (children, testid) => (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid={testid}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/site-assets/logo.png" alt="Wales Event Medical Services" className="h-12 w-auto" />
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-4 sm:p-6">{children}</main>
    </div>
  );

  if (err) return shell(
    <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="questions-error">
      <h1 className="text-xl font-bold text-[#0F2547]">Link not working</h1>
      <p className="text-sm text-slate-500 mt-2">{err} — please contact Wales Event Medical Services and we'll send a fresh one.</p>
    </div>, "questions-form");

  if (!form) return shell(<p className="text-sm text-slate-500">Loading…</p>, "questions-form");

  if (done || form.answered) return shell(
    <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="questions-success">
      <CheckCircle2 className="h-12 w-12 text-[#007F3B] mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-[#0F2547]">Thank you — answers received</h1>
      <p className="text-sm text-slate-500 mt-2">Wales Event Medical Services has your answers and will be in touch with your quote shortly.</p>
    </div>, "questions-form");

  return shell(
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4" data-testid="questions-form-body">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]">{form.title || "A few quick questions"}</h1>
        {form.event_name && (
          <p className="text-xs text-slate-500 mt-1" data-testid="questions-event-line">
            About: <strong>{form.event_name}</strong>{form.event_date ? ` — ${form.event_date}` : ""}
          </p>
        )}
        {form.comment && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap" data-testid="questions-comment">{form.comment}</p>}
        <p className="text-xs text-slate-400 mt-2">Takes a couple of minutes — your answers help us quote accurately.</p>
      </div>
      {form.questions.map((q, i) => (
        <label key={i} className="block" data-testid={`questions-q-${i}`}>
          <span className="text-sm font-semibold text-[#0F2547] block mb-1">{i + 1}. {q}</span>
          <textarea rows={2} value={answers[i]} onChange={(e) => setAnswers((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
            placeholder="Your answer…" data-testid={`questions-answer-${i}`}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
        </label>
      ))}
      <button type="submit" disabled={sending || !answers.some((a) => a.trim())} data-testid="questions-submit-btn"
        className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
        <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send my answers"}
      </button>
    </form>, "questions-form");
}
